import { FormEvent, useMemo, useState } from 'react'; // Hooks: estado controlado y memo para reglas
import { useNavigate, useSearchParams } from 'react-router-dom'; // Navegación y lectura de query string
import { isAxiosError } from 'axios'; // Cliente HTTP para invocar /auth/reset
import { toast } from 'sonner'; // Notificaciones amigables
import { Loader2 } from 'lucide-react'; // Icono de “cargando” reutilizado
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx'; // Layout consistente
import { Input } from '../ui/input.tsx'; // Campos estilizados
import { Label } from '../ui/label.tsx'; // Etiquetas accesibles
import { Button } from '../ui/button.tsx'; // Botón principal
import apiClient from '../../services/apiClient.ts';

const MIN_PASSWORD_LENGTH = 8; // Regla mínima de seguridad

export function ResetPassword() {
  const [searchParams] = useSearchParams(); // Obtiene parámetros ?token=...
  const token = searchParams.get('token') ?? ''; // Extrae token o cadena vacía
  const navigate = useNavigate(); // Permite redirigir tras éxito

  const [newPassword, setNewPassword] = useState(''); // Controla campo “Nueva contraseña”
  const [confirmPassword, setConfirmPassword] = useState(''); // Controla “Confirmar contraseña”
  const [isSubmitting, setIsSubmitting] = useState(false); // Bloquea formulario en envío

  const passwordHint = useMemo(
    () => `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`,
    []
  ); // Texto reutilizable para ayudas

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); // Evita refresh por submit nativo
    if (isSubmitting) return; // Evita doble envío accidental

    if (!token) {
      toast.error('El enlace de recuperación es inválido o ha caducado.');
      return;
    } // Protege contra URL sin token

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      toast.error(passwordHint);
      return;
    } // Valida longitud mínima

    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden.');
      return;
    } // Verifica coincidencia

    setIsSubmitting(true); // Muestra spinner y bloquea botón
    try {
      await apiClient.post('/auth/reset', {
        token,
        new_password: newPassword,
      }); // Invoca backend con payload requerido

      toast.success('Tu contraseña fue actualizada. Inicia sesión con ella.'); // Feedback positivo
      navigate('/login', { replace: true }); // Redirige a login sin permitir back al form
    } catch (error) {
      console.error(error); // Útil en desarrollo/debug
      if (isAxiosError(error) && error.response?.status === 400) {
        toast.error('El enlace ya no es válido. Solicita uno nuevo.');
      } else {
        toast.error('No pudimos actualizar la contraseña. Intenta más tarde.');
      } // Mensajes según escenario
    } finally {
      setIsSubmitting(false); // Permite reintentar
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    !token ||
    !newPassword ||
    !confirmPassword; // Deshabilita botón si falta info

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle>Define tu nueva contraseña</CardTitle> {/* Contexto claro */}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password">Nueva contraseña</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                placeholder="****************"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                disabled={!token || isSubmitting}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{passwordHint}</p> {/* Ayuda visual */}
            </div>

            <div>
              <Label htmlFor="confirm-password">Confirmar contraseña</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                placeholder="****************"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={!token || isSubmitting}
                required
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitDisabled}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando
                </>
              ) : (
                'Restablecer contraseña'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default ResetPassword;
